import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { donorApplication } from "../apiPath/api";

export const getAllDonorApplication = createAsyncThunk(
  "donorApplication/getAllDonorApplications",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await donorApplication.getAllDonorApplications(params);
      return response.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Failed to fetch donor applications";
      return rejectWithValue(backendMsg);
    }
  }
);

export const getDonorApplicationById = createAsyncThunk(
  "donorApplication/getDonorApplicationById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await donorApplication.getDonorApplicationById(id);
      return response.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Failed to fetch donor application";
      return rejectWithValue(backendMsg);
    }
  }
);

export const createDonorApplication = createAsyncThunk(
  "donorApplication/createDonorApplication",
  async (data, { rejectWithValue }) => {
    try {
      const response = await donorApplication.createDonorApplication(data);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create donor application";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const deleteDonorApplication = createAsyncThunk(
  "donorApplication/deleteDonorApplication",
  async (id, { rejectWithValue }) => {
    try {
      // Adjust API call as per your API utility
      const response = await donorApplication.deleteDonorApplication(id);
      return response.data.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(backendMsg);
    }
  }
);

export const undoDeleteDonorApplication = createAsyncThunk(
  "donorApplication/undoDeleteDonorApplication",
  async (id, { rejectWithValue }) => {
    try {
      const response = await donorApplication.undoDeleteDonorApplication(id);
      return response.data.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(backendMsg);
    }
  }
);

export const updateDonorApplication = createAsyncThunk(
  "donorApplication/updateDonorApplication",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await donorApplication.updateDonorApplication(id, data);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update donor application";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const approveDonorApplication = createAsyncThunk(
  "donorApplication/approveDonorApplication",
  async ({ id, processedBy, notes }, { rejectWithValue }) => {
    try {
      const response = await donorApplication.approveDonorApplication(id, { processedBy, notes });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to approve donor application";
      return rejectWithValue(message);
    }
  }
);

export const rejectDonorApplication = createAsyncThunk(
  "donorApplication/rejectDonorApplication",
  async ({ id, processedBy, notes }, { rejectWithValue }) => {
    try {
      const response = await donorApplication.rejectDonorApplication(id, { processedBy, notes });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to approve donor application";
      return rejectWithValue(message);
    }
  }
);


const donorApplicationSlice = createSlice({
  name: "donorApplication",
  initialState: {
    loading: false,
    error: null,
    success: false,
    donorApplications: [],
    currentDonorApplication: null,
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    statusCounts: { all: 0, pending: 0, approved: 0, rejected: 0 },
  },

  reducers: {
    resetDonorApplicationState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.currentDonorApplication = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Create Donor Application
      .addCase(createDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createDonorApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update Donor Application
      .addCase(updateDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDonorApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get All Donor Applications
      .addCase(getAllDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllDonorApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.donorApplications = action.payload.data;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts;
        state.error = null;
      })
      .addCase(getAllDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Donor Application By ID
      .addCase(getDonorApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDonorApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDonorApplication = action.payload.data;
        state.error = null;
      })
      .addCase(getDonorApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Donor Application
      .addCase(deleteDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDonorApplication.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Undo Delete Donor Application
      .addCase(undoDeleteDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(undoDeleteDonorApplication.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(undoDeleteDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve Donor Application
      .addCase(approveDonorApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveDonorApplication.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.success = true;
      })
      .addCase(approveDonorApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetDonorApplicationState } = donorApplicationSlice.actions;
export default donorApplicationSlice.reducer;
